import React, { useState, useEffect } from 'react'
import { getCustomerDetails, updateCustomerDetails } from '../../lib/spektrix'

const EditDetails = ({ user }) => {
  const [formData, setFormData] = useState({
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    mobile: '',
    dateOfBirth: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [message, setMessage] = useState(null)
  const [errors, setErrors] = useState({})

  useEffect(() => {
    // Load customer details
    const loadDetails = async () => {
      setIsLoading(true)
      try {
        const details = await getCustomerDetails()
        setFormData({
          title: details.title || '',
          firstName: details.firstName || '',
          lastName: details.lastName || '',
          email: details.email || '',
          phone: details.phone || '',
          mobile: details.mobile || '',
          dateOfBirth: details.birthDate ? details.birthDate.split('T')[0] : ''
        })
      } catch (error) {
        console.error('Failed to load customer details:', error)
        setMessage({ type: 'error', text: 'Failed to load your details. Please try again.' })
      } finally {
        setIsLoading(false)
      }
    }

    loadDetails()
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const validateForm = () => {
    const newErrors = {}
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required'
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required'
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setIsSaving(true)
    setMessage(null)
    
    try {
      // Prepare update data
      const updateData = {
        title: formData.title,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        mobile: formData.mobile
      }
      
      // Only include birthDate if it has a value
      if (formData.dateOfBirth) {
        updateData.birthDate = formData.dateOfBirth
      }
      
      await updateCustomerDetails(updateData)
      setMessage({ type: 'success', text: 'Your details have been updated successfully.' })
      
      // Dispatch event to update auth state with new details
      window.dispatchEvent(new CustomEvent('spektrix-auth-change', {
        detail: {
          authenticated: true,
          user: { ...user, ...updateData }
        }
      }))
    } catch (error) {
      console.error('Failed to update details:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to update your details. Please try again.' })
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <span className="loading loading-spinner loading-md"></span>
      </div>
    )
  }

  const inputClasses = "input input-bordered w-full bg-gray-700 text-white border-gray-600 focus:border-blue-500 focus:outline-none"
  const labelClasses = "label-text text-gray-300"

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6 text-white">Personal Details</h2>
      
      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'} mb-4`}>
          <span className="text-white">{message.text}</span>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="form-control">
            <label className="label">
              <span className={labelClasses}>Title</span>
            </label>
            <select
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="select select-bordered w-full bg-gray-700 text-white border-gray-600 focus:border-blue-500 focus:outline-none"
            >
              <option value="">Select title</option>
              <option value="Mr">Mr</option>
              <option value="Mrs">Mrs</option>
              <option value="Miss">Miss</option>
              <option value="Ms">Ms</option>
              <option value="Dr">Dr</option>
              <option value="Prof">Prof</option>
              <option value="Rev">Rev</option>
            </select>
          </div>
          
          <div className="form-control">
            <label className="label">
              <span className={labelClasses}>Date of Birth</span>
            </label>
            <input
              type="date"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={handleChange}
              className={inputClasses}
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="form-control">
            <label className="label">
              <span className={labelClasses}>First Name *</span>
            </label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className={`${inputClasses} ${errors.firstName ? 'input-error' : ''}`}
              required
            />
            {errors.firstName && (
              <label className="label">
                <span className="label-text-alt text-error">{errors.firstName}</span>
              </label>
            )}
          </div>
          
          <div className="form-control">
            <label className="label">
              <span className={labelClasses}>Last Name *</span>
            </label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className={`${inputClasses} ${errors.lastName ? 'input-error' : ''}`}
              required
            />
            {errors.lastName && (
              <label className="label">
                <span className="label-text-alt text-error">{errors.lastName}</span>
              </label>
            )}
          </div>
        </div>
        
        <div className="form-control">
          <label className="label">
            <span className={labelClasses}>Email Address *</span>
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`${inputClasses} ${errors.email ? 'input-error' : ''}`}
            required
          />
          {errors.email && (
            <label className="label">
              <span className="label-text-alt text-error">{errors.email}</span>
            </label>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="form-control">
            <label className="label">
              <span className={labelClasses}>Phone</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className={inputClasses}
            />
          </div>
          
          <div className="form-control">
            <label className="label">
              <span className={labelClasses}>Mobile</span>
            </label>
            <input
              type="tel"
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              className={inputClasses}
            />
          </div>
        </div>
        
        <div className="form-control mt-6">
          <button
            type="submit"
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default EditDetails